import { Injectable } from '@angular/core';
import { PrintOwnPhotosBasketService } from '@ui/print-own-photos/print-own-photos-basket.service';
import { filter, first } from 'rxjs/operators';
import { Subject } from 'rxjs';
import cuid from 'cuid';

type AppEvent =
  | { kind: 'click'; x: number; y: number }
  | { kind: 'keypress'; key: string; code: number }
  | { kind: 'focus'; element: HTMLElement };

function handleEvent(event: AppEvent) {
  switch (event.kind) {
    case 'click':
      // We know it is a mouse click, so we can access `x` and `y` now
      console.log(`Mouse clicked at (${event.x}, ${event.y})`);
      break;
    case 'keypress':
      // We know it is a key press, so we can access `key` and `code` now
      console.log(`Key pressed: (key=${event.key}, code=${event.code})`);
      break;
    case 'focus':
      // We know it is a focus event, so we can access `element`
      console.log(`Focused element: ${event.element.tagName}`);
      break;
  }
}

interface IframeMessageAskForFile {
  type: 'askForFile';
  originalName: string;
  size: number;
}

interface IframeMessageSendFile {
  type: 'sendFile';
  file: File;
}
interface IframeMessageSetupChildBridge {
  type: 'setupChildBridge';
}
interface IframeMessageGoBackToHomePage {
  type: 'goBackToHomePage';
  url: string;
}

type IframeMessage =
  | (
      | IframeMessageAskForFile
      | IframeMessageSendFile
      | IframeMessageSetupChildBridge
      | IframeMessageGoBackToHomePage
    ) & {
      jobId: string;
    };

@Injectable({
  providedIn: 'root',
})
export class IFrameCommunicationService {
  jobs$ = new Subject<{
    id: string;
    payload: any;
  }>();
  currentDomain = '';
  parentFrameCreated = new Subject();
  constructor(basketService: PrintOwnPhotosBasketService) {
    console.log('Bridge connected');
    window.addEventListener(
      'message',
      ({ data, source }: MessageEvent<IframeMessage>) => {
        console.log({ data, source });
        console.log(data.type, location.origin);
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);

        const parentFrame = urlParams.get('parentFrame');

        if (data.type === 'askForFile') {
          basketService.table.toArray().then((basket) => {
            const toUpload = basket.find(
              ({ file }) =>
                file?.name === data.originalName && file?.size === data.size,
            );
            console.log(toUpload);
            const url = new URL(parentFrame);
            if (toUpload && source && 'window' in source) {
              source.postMessage(
                {
                  type: 'sendFile',
                  file: toUpload.file,
                  jobId: data.jobId,
                } as IframeMessageSendFile,
                url.origin,
                // [toUpload.file]
              );
              console.log(source);
            }
          });
        } else if (data.type === 'sendFile') {
          console.log(data);
          this.jobs$.next({
            id: data.jobId,
            payload: data.file,
          });
        } else if (data.type === 'setupChildBridge') {
          console.log(data, window.self !== window.top);
          if (window.self !== window.top) {
            this.parentFrameCreated.next(undefined);
            console.log('PARENT FRAME CONNECTED');
          }
        } else if (data.type === 'goBackToHomePage') {
          window.location.href = data.url;
        }
      },
    );
    this.setupParentBridgeFromHost();
  }

  async setupChildBridge(domain: string) {
    console.log('SETUP BRIDGE');
    this.currentDomain = domain;
    const iframe = document.createElement('iframe');
    if (domain.includes('zalamo.com')) {
      iframe.src = `${location.protocol}//${domain}/iframe-bridge`;
    }

    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.style.position = 'absolute';
    iframe.style.top = '-100px';
    iframe.style.left = '-100px';
    iframe.style.border = '0';
    iframe.name = 'bridge';
    document.body.appendChild(iframe);
    await new Promise<void>((resolve) => {
      iframe.onload = () => {
        resolve();
      };
    });
  }
  setupParentBridge(domain: string) {
    if (window.self !== window.top) {
      const queryString = window.location.search;

      const urlParams = new URLSearchParams(queryString);
      window.parent.postMessage(
        { type: 'setupChildBridge' },
        urlParams.get('hostDomain'),
      );
      return this.parentFrameCreated;
    } else {
      location.href = `${location.protocol}//${domain}/iframe-bridge?parentFrame=${location.href}`;
    }
  }
  async setupParentBridgeFromHost() {
    const queryString = window.location.search;

    const urlParams = new URLSearchParams(queryString);
    if (urlParams.has('parentFrame')) {
      const parentFrame = urlParams.get('parentFrame');
      console.log(parentFrame);
      const iframe = document.createElement('iframe');
      const url = new URL(parentFrame);
      url.searchParams.append('hostDomain', location.origin);
      iframe.src = url.toString();
      iframe.setAttribute('frameBorder', '0');
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.position = 'fixed';
      iframe.style.top = '0';
      iframe.style.left = '0';
      iframe.name = 'bridge';
      document.body.appendChild(iframe);
      await new Promise<void>((resolve) => {
        window.addEventListener(
          'message',
          ({ data, source }: MessageEvent<IframeMessage>) => {
            if (data.type === 'setupChildBridge') {
              // @ts-ignore
              iframe.contentWindow.postMessage(
                {
                  type: 'setupChildBridge',
                },
                url.origin,
              );
              resolve();
            }
          },
        );
      });
    }
  }

  goBackToHomeUrl() {
    const queryString = window.location.search;

    const urlParams = new URLSearchParams(queryString);
    const parentFrame = urlParams.get('hostDomain');

    window.parent.postMessage(
      { type: 'goBackToHomePage', url: location.href },
      parentFrame,
    );
  }
  askForFile(size, originalName) {
    // @ts-ignore
    console.log(window.parent);
    const jobId = cuid();
    const queryString = window.location.search;

    const urlParams = new URLSearchParams(queryString);
    // @ts-ignore
    window.parent.postMessage(
      { type: 'askForFile', size, originalName, jobId },
      urlParams.get('hostDomain'),
    );
    return this.jobs$.pipe(
      filter((job) => job.id === jobId),
      first(),
    );
  }
}
